import {
  Account,
  Assistant,
  Avatars,
  BillingPlan,
  Client,
  Console,
  Domains,
  Functions,
  Health,
  ID,
  Locale,
  Migrations,
  Organizations,
  Platform,
  Projects,
  Query,
  Realtime,
  Region,
  Sites,
  Teams,
  Users,
} from "@appwrite.io/console";
import type { Models } from "@appwrite.io/console";
import { imagineConfigStore } from "@/stores/imagine-config.store";

function getConfig() {
  const state = imagineConfigStore.getState();
  if (!state.initialized || !state.config) {
    throw new Error("ImagineConfig must be initialized before use");
  }
  return state.config;
}

function createConsoleSdk(client: Client) {
  return {
    client: client,
    account: new Account(client),
    avatars: new Avatars(client),
    functions: new Functions(client),
    health: new Health(client),
    locale: new Locale(client),
    projects: new Projects(client),
    organizations: new Organizations(client),
    teams: new Teams(client),
    users: new Users(client),
    migrations: new Migrations(client),
    console: new Console(client),
    assistant: new Assistant(client),
    sites: new Sites(client),
    domains: new Domains(client),
    realtime: new Realtime(client),
  };
}

export const createConsoleClient = () => {
  const config = getConfig();
  const clientConsole = new Client();
  clientConsole.setEndpoint(config.APPWRITE_ENDPOINT).setProject("console");
  return createConsoleSdk(clientConsole);
};

export const createProjectClient = (projectId: string) => {
  const config = getConfig();
  const clientProject = new Client();
  clientProject
    .setEndpoint(config.APPWRITE_ENDPOINT)
    .setMode("admin")
    .setProject(projectId);
  return createConsoleSdk(clientProject);
};

export const getProjects = async (
  client: Client,
  excludeProjectId?: string,
) => {
  const projects = new Projects(client);

  const queries = [Query.orderDesc("$updatedAt"), Query.limit(100)];

  if (excludeProjectId) {
    queries.push(Query.notEqual("$id", excludeProjectId));
  }

  const projectsList = await projects.list({
    queries,
  });
  return projectsList.projects;
};

export const getProject = async (client: Client, projectId: string) => {
  const projects = new Projects(client);
  const project = await projects.get({ projectId });
  return project;
};

export const getCurrentProject = async (client: Client, projectId: string) => {
  const projects = new Projects(client);
  const project = await projects.get({ projectId });
  return project;
};

export async function createAppwriteProject({ id }: { id: string }) {
  const client = createConsoleClient();
  const orgs = await client.organizations.list({
    queries: [Query.equal("platform", Platform.Imagine)],
  });
  let team: Models.Team;

  if (orgs.total === 0) {
    team = await createDefaultTeam(client.client);
  } else {
    team = orgs.teams[0] as Models.Team;
  }

  const defaultRegion = Region.Fra;

  const project = await client.projects.create({
    projectId: id,
    name: "New project",
    teamId: team.$id,
    region: defaultRegion,
  });

  return project;
}

async function createDefaultTeam(client: Client) {
  const orgsSdk = new Organizations(client);
  const team = await orgsSdk.create({
    organizationId: ID.unique(),
    name: "Personal projects",
    platform: Platform.Imagine,
    billingPlan: BillingPlan.Imaginetier0,
  });
  return team;
}
